import {
    useEffect,
    useLayoutEffect,
    useState
} from "react";
import {
    TouchableOpacity,
    Image,
    View,
    ActivityIndicator,
    ScrollView,
    Modal
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    RowCard,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute,
    type RouteProp
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    GetMemberProfilePayloadType
} from "../../../services/restAPI/actions/getMemberProfile/types";
import type AppStackParamList from "../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronRight,
    ChevronLeft,
    X
} from "lucide-react-native";
import {
    GestureHandlerRootView
} from "react-native-gesture-handler";
import {
    FooterVersionInfo,
    ZoomableImage
} from "../../../components";
import {
    RESTService
} from "../../../services/restAPI";

const UserProfile = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const route = useRoute<RouteProp<AppStackParamList, "UserProfile">>();

    const {
        spaces,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        userID
    } = route.params;
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [data,
        setData] = useState<GetMemberProfilePayloadType>();
    const [loading,
        setLoading] = useState(true);
    const [isLightboxVisible,
        setIsLightboxVisible] = useState(false);

    const inset = useSafeAreaInsets();

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={{
                    paddingTop: inset.top,
                    paddingHorizontal: spaces.spacingMd,
                    backgroundColor: colors.content.container.default
                }}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
            </View>
        });
    }, [navigation]);

    useEffect(() => {
        getProfileData();
    }, []);

    const getProfileData = () => {
        RESTService.action("GetMemberProfile", {
            userID: userID
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setData(res.payload);
                }
                setLoading(false);
            }).catch((err) => {
                console.log(err);
            });
    };

    const renderUserInitalInfo = () => {
        return <View
            style={[
                stylesheet.userInitialInfoContainer,
                {
                    marginTop: spaces.spacingLg,
                    borderColor: colors.content.border.subtle,
                    marginBottom: spaces.spacingMd
                }
            ]}
        >
            <TouchableOpacity
                onPress={() => {
                    setIsLightboxVisible(true);
                }}
            >
                <Image
                    source={{
                        uri: data?.profilePhotoURL
                    }}
                    style={[
                        stylesheet.profilePhoto,
                        {
                            borderRadius: radiuses.full,
                            marginRight: spaces.spacingSm * 1.5
                        }
                    ]}
                />
            </TouchableOpacity>
            <Text
                variant="headlineSmallSize"
                color="high"
                style={{
                    marginBottom: spaces.spacingXs
                }}
            >
                {data?.fullName}
            </Text>
            <Text
                variant="bodyMediumSize"
                color="mid"
                style={{
                    marginBottom: spaces.spacingXs
                }}
            >
                {(data?.companies && data.companies.length > 0 && data.companies[0]?.companyTitle) ? data.companies[0]?.companyTitle : localize("notSpecified")}
            </Text>
            <Text
                variant="bodyMediumSize"
                color="low"
            >
                {data?.mail}
            </Text>
        </View>;
    };

    //TODO: Profil fırsatları resimler geldiğinde tamamla
    const renderCommercialOpportunities = () => {
        if(data?.myCommercialOpportunities && data.myCommercialOpportunities.length) {
            return <View
                style={{
                    marginBottom: spaces.spacingLg
                }}
            >
                <Text
                    variant="titleMediumSize"
                    color="high"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                >
                    {localize("jobOpportunity")}
                </Text>
                <View
                    style={[
                        stylesheet.campaignImageWrapper,
                        {
                            backgroundColor: colors.content.container.subtle,
                            borderRadius: radiuses.xl
                        }
                    ]}
                >
                    <Image
                        source={{
                            uri: data?.myCommercialOpportunities[0]?.coverPhotoURL
                        }}
                        resizeMode="cover"
                        style={[
                            stylesheet.campaignImageRadius,
                            {
                                borderRadius: radiuses.lg
                            }
                        ]}
                    />
                    <View
                        style={[
                            stylesheet.campaignTag,
                            {
                                paddingHorizontal: spaces.spacingMd,
                                paddingVertical: spaces.spacingSm,
                                backgroundColor:colors.content.container.default,
                                borderRadius: radiuses.lg,
                                left: spaces.spacingMd,
                                top: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            color="high"
                            variant="titleMediumSize"
                        >
                            {data?.myCommercialOpportunities[0]?.title}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.campaignBottom,
                            {
                                left: spaces.spacingMd,
                                bottom: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            color="onPrimary"
                            variant="bodyMediumSize"
                        >
                            {data?.myCommercialOpportunities[0]?.title}
                        </Text>
                        <Text
                            color="onPrimary"
                            variant="bodyMediumSize"
                        >
                            {data?.myCommercialOpportunities[0]?.description}
                        </Text>
                    </View>
                </View>
            </View>;
        } else {
            return;
        }

    };

    const renderAboutUser = () => {
        return <View
            style={{
                marginBottom: spaces.spacingLg
            }}
        >
            <Text
                variant="titleMediumSize"
                color="high"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                {localize("about")}
            </Text>
            <Text>
                {data?.aboutMyTrade}
            </Text>
        </View>;
    };

    const renderUserCompanies = () => {
        return <View
            style={{
                marginBottom: spaces.spacingMd
            }}
        >
            <View
                style={stylesheet.userCompaniesTitleContainer}
            >
                <Text
                    variant="titleMediumSize"
                    color="high"
                >
                    {localize("affiliatedCompanies")}
                </Text>
            </View>
            {
                data && data.companies && data.companies.length ?
                    data.companies.map((e) => {
                        return <RowCard
                            key={e.companyID}
                            isTransparentBackground={true}
                            subTitle={e.companyTitle}
                            title={e.position}
                            titleColor="high"
                            icon={() => <View>
                                <Image
                                    source={{
                                        uri: e.companyLogo
                                    }}
                                    resizeMode="contain"
                                    style={[
                                        stylesheet.companyLogo,
                                        {
                                            borderRadius: radiuses.xs
                                        }
                                    ]}
                                />
                            </View>}
                            onPress={() => {
                                navigation.navigate("CompanyDetail", {
                                    companyID: e.companyID
                                });
                            }}
                            rightIcon={() => {
                                return <ChevronRight
                                    color={colors.content.icon.high}
                                />;
                            }}
                        />;
                    })
                    : null
            }
        </View>;
    };

    if(loading) {
        return <PageContainer
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                size={40}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <PageContainer
        isScrollable={false}
        backgroundColor="default"
        style={stylesheet.container}
    >
        <ScrollView
            style={stylesheet.flex1}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
        >
            {renderUserInitalInfo()}
            {renderCommercialOpportunities()}
            {renderAboutUser()}
            {renderUserCompanies()}
        </ScrollView>
        <FooterVersionInfo/>
        <Modal
            visible={isLightboxVisible}
            transparent={true}
            animationType="fade"
            onRequestClose={() => setIsLightboxVisible(false)}
        >
            <GestureHandlerRootView
                style={stylesheet.flex1}
            >
                <View
                    style={stylesheet.lightboxContainer}
                >
                    <TouchableOpacity
                        style={[
                            stylesheet.lightboxCloseBtn,
                            {
                                top: inset.top + spaces.spacingMd,
                                right: spaces.spacingMd,
                                padding: spaces.spacingSm
                            }
                        ]}
                        onPress={() => setIsLightboxVisible(false)}
                    >
                        <X
                            size={30}
                            color="#FFF"
                        />
                    </TouchableOpacity>
                    {data?.profilePhotoURL && (
                        <ZoomableImage
                            uri={data.profilePhotoURL}
                        />)}
                </View>
            </GestureHandlerRootView>
        </Modal>
    </PageContainer>;
};
export default UserProfile;
